import { createTheme, type ThemeOptions } from "@mui/material/styles"
import { orange } from "@mui/material/colors"

// Extract color palette to avoid importing the full grey module
const greyColors = {
  500: "#9e9e9e",
  700: "#616161",
}

// Define theme colors as constants for consistency
const COLORS = {
  background: {
    paper: "#1C1C1E",
    default: "#121212",
  },
  gold: {
    main: "#E5BF77",
    dark: "#DDAB4C",
    light: "#F7D883",
  },
  text: {
    primary: "#E0E0E0",
    secondary: "#E5BF77",
  },
}

// Create a function to generate the theme options
function createThemeOptions(): ThemeOptions {
  return {
    palette: {
      mode: "dark",
      background: {
        paper: COLORS.background.paper,
        default: COLORS.background.default,
      },
      primary: {
        main: orange[500],
        dark: orange[700],
        light: orange[400],
        contrastText: "#000",
      },
      secondary: {
        main: COLORS.gold.main,
        dark: COLORS.gold.dark,
        light: COLORS.gold.light,
        contrastText: "#000",
      },
      text: {
        primary: COLORS.text.primary,
        secondary: COLORS.gold.main,
        disabled: greyColors[500],
      },
      warning: {
        main: "#ffa726",
        contrastText: "#000",
      },
      success: {
        light: "#77E588",
        main: "#66bb6a",
        dark: "#33691e",
        contrastText: "#000",
      },
      info: {
        main: "#779DE5",
        contrastText: "#000",
      },
    },
    typography: {
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      
      h1: {
        fontWeight: 700,
        color: "#E59705",
        fontSize: "2.5rem",
        lineHeight: 1.2,
        letterSpacing: "-0.012em",
        marginBottom: "0.8rem",
        marginTop: "2.8rem",
      },
      h2: {
        fontWeight: 700,
        color: "#E59705",
        fontSize: "2rem",
        lineHeight: 1.25,
        letterSpacing: "-0.00833em",
        marginTop: "1rem",
      },
      h3: {
        fontWeight: 700,
        color: "#E59705",
        fontSize: "1.75rem",
        lineHeight: 1.29,
        letterSpacing: "-0.005em",
        marginTop: "1rem",
        marginBottom: "0.8rem",
      },
      h4: {
        fontWeight: 700,
        color: "#E59705",
        fontSize: "1.5rem",
        lineHeight: 1.33,
        letterSpacing: "-0.0025em",
      },
      h5: {
        fontWeight: 700,
        color: "#E59705",
        fontSize: "1.25rem",
        lineHeight: 1.4,
      },
      h6: {
        fontWeight: 700,
        color: "#E59705",
        fontSize: "1rem",
        lineHeight: 1.5,
      },
      body1: {
        fontWeight: 400,
        fontSize: "1.125rem",
        lineHeight: 1.56,
        letterSpacing: "-0.00735em",
        color: COLORS.text.primary,
      },
      body2: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: "-0.00833em",
        color: COLORS.text.primary,
      },
      subtitle1: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: "-0.00833em",
        color: COLORS.text.primary,
      },
    },
    components: {
      // Global styles and performance optimizations
      MuiCssBaseline: {
        styleOverrides: `
          body {
            opacity: 0.9;
          }
          
          /* Global performance optimizations */
          [class*="Mui"] {
            transition-duration: 0.1s !important;
            transition-timing-function: ease-out !important;
          }
          
          /* Box component optimizations */
          .MuiBox-root {
            contain: layout !important;
            will-change: transform !important;
            touch-action: manipulation !important;
          }
          
          /* Typography optimizations */
          .MuiTypography-root {
            contain: content !important;
            will-change: transform !important;
            touch-action: manipulation !important;
          }
          
          /* Button optimizations */
          .MuiButtonBase-root {
            contain: content !important;
            will-change: transform, background-color !important;
            touch-action: manipulation !important;
            transform: translateZ(0) !important;
            transition: transform 0.1s ease-out, background-color 0.1s ease-out !important;
          }
          
          /* Grid optimizations */
          .MuiGrid-root {
            contain: layout !important;
            will-change: transform !important;
            touch-action: manipulation !important;
          }
          
          /* Chip optimizations */
          .MuiChip-root {
            contain: content !important;
            will-change: transform !important;
            touch-action: manipulation !important;
            transform: translateZ(0) !important;
          }
          
          /* Paper optimizations */
          .MuiPaper-root {
            contain: content !important;
            will-change: transform !important;
            touch-action: manipulation !important;
          }
          
          /* Container optimizations */
          .MuiContainer-root {
            contain: layout !important;
            will-change: transform !important;
            touch-action: manipulation !important;
          }
          
          /* Card optimizations */
          .MuiCard-root {
            contain: content !important;
            will-change: transform !important;
            touch-action: manipulation !important;
          }
          
          /* Modal and Backdrop optimizations */
          .MuiBackdrop-root {
            contain: strict !important;
            will-change: opacity !important;
            transform: translateZ(0) !important;
            transition-duration: 0.1s !important;
            transition-property: opacity !important;
            transition-timing-function: ease-out !important;
            touch-action: none !important;
            background-color: rgba(0, 0, 0, 0.5) !important;
          }
          
          .MuiModal-root {
            contain: layout !important;
            touch-action: none !important;
          }
          
          .MuiDialog-paper {
            contain: content !important;
            will-change: transform !important;
            transform: translateZ(0) !important;
          }
          
          /* Force hardware acceleration on interactive elements */
          .MuiButtonBase-root, 
          .MuiIconButton-root, 
          .MuiChip-root,
          a[class*="Mui"],
          button[class*="Mui"] {
            transform: translateZ(0) !important;
          }
          
          /* Responsive typography */
          @media (max-width: 600px) {
            h1 { font-size: 2rem; line-height: 2.6rem; margin-top: 1rem; margin-bottom: 0.8rem; }
            h2 { font-size: 1.75rem; line-height: 2.25rem; margin-top: 1rem; margin-bottom: 0.8rem; }
            h3 { font-size: 1.5rem; line-height: 2rem; margin-top: 0.9rem; margin-bottom: 0.2rem; }
            h4 { font-size: 1.375rem; line-height: 1.875rem; margin-top: 0.8rem; margin-bottom: 0.6rem; }
            h5 { font-size: 1.125rem; line-height: 1.5rem; margin-top: 0.7rem; margin-bottom: 0.5rem; }
            h6 { font-size: 0.875rem; line-height: 1.25rem; margin-top: 0.6rem; margin-bottom: 0.4rem; }
          }
          
          @media (max-width: 400px) {
            h1 { font-size: 1.75rem; line-height: 2.25rem; margin-top: 0.8rem; margin-bottom: 0.6rem; }
            h2 { font-size: 1.5rem; line-height: 2rem; margin-top: 0.9rem; margin-bottom: 0.7rem; }
            h3 { font-size: 1.375rem; line-height: 1.875rem; margin-top: 0.8rem; margin-bottom: 0.5rem; }
            h4 { font-size: 1.25rem; line-height: 1.75rem; margin-top: 0.7rem; margin-bottom: 0.5rem; }
            h5 { font-size: 1rem; line-height: 1.375rem; margin-top: 0.6rem; margin-bottom: 0.4rem; }
            h6 { font-size: 0.75rem; line-height: 1.125rem; margin-top: 0.5rem; margin-bottom: 0.3rem; }
          }
        `,
      },

      // Component-specific optimizations using valid TypeScript keys
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            contain: "content",
            willChange: "transform",
            touchAction: "manipulation",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "16px 0px 16px 0px",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            padding: 0,
            margin: 0,
            contain: "content",
            willChange: "transform",
            touchAction: "manipulation",
          },
        },
      },
      MuiCardMedia: {
        styleOverrides: {
          root: {
            position: "relative",
            contain: "content",
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            overflow: "hidden",
            padding: 0,
            margin: 0,
            contain: "content",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
            contain: "layout",
            willChange: "transform",
            touchAction: "manipulation",
          },
        },
      },

      // Button optimizations
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
        },
        styleOverrides: {
          root: {
            contain: "content",
            willChange: "transform, background-color",
            touchAction: "manipulation",
            transform: "translateZ(0)",
            transition: "transform 0.1s ease-out, background-color 0.1s ease-out",
          },
        },
      },

      // Typography optimizations
      MuiTypography: {
        styleOverrides: {
          root: {
            contain: "content",
            willChange: "transform",
            touchAction: "manipulation",
          },
        },
      },

      // Grid optimizations
      MuiGrid: {
        styleOverrides: {
          root: {
            contain: "layout",
            willChange: "transform",
            touchAction: "manipulation",
          },
        },
      },

      // Chip optimizations
      MuiChip: {
        styleOverrides: {
          root: {
            contain: "content",
            willChange: "transform",
            touchAction: "manipulation",
            transform: "translateZ(0)",
          },
          label: {
            contain: "content",
          },
        },
      },

      // Container optimizations
      MuiContainer: {
        styleOverrides: {
          root: {
            contain: "layout",
            willChange: "transform",
            touchAction: "manipulation",
          },
        },
      },

      // Modal optimizations
      MuiModal: {
        styleOverrides: {
          root: {
            contain: "layout",
            touchAction: "none",
          },
        },
        defaultProps: {
          disableAutoFocus: true,
          disableRestoreFocus: true,
          disableEnforceFocus: true,
          disableScrollLock: true,
          keepMounted: false,
          closeAfterTransition: true,
        },
      },

      // Backdrop optimizations
      MuiBackdrop: {
        styleOverrides: {
          root: {
            transform: "translateZ(0)",
            contain: "strict",
            transitionProperty: "opacity",
            transitionDuration: "0.1s",
            transitionTimingFunction: "ease-out",
            touchAction: "none",
            willChange: "opacity",
            backdropFilter: "none",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      },

      // Dialog optimizations
      MuiDialog: {
        styleOverrides: {
          root: {
            contain: "layout",
            touchAction: "none",
          },
          paper: {
            transform: "translateZ(0)",
            contain: "content",
            willChange: "transform",
          },
        },
        defaultProps: {
          disableAutoFocus: true,
          disableRestoreFocus: true,
          disableEnforceFocus: true,
          disableScrollLock: true,
          keepMounted: false,
          closeAfterTransition: true,
        },
      },

      // Drawer optimizations
      MuiDrawer: {
        styleOverrides: {
          root: {
            contain: "layout",
            touchAction: "none",
          },
          paper: {
            transform: "translateZ(0)",
            contain: "content",
            willChange: "transform",
          },
        },
        defaultProps: {
          disableAutoFocus: true,
          disableRestoreFocus: true,
          disableEnforceFocus: true,
          disableScrollLock: true,
          keepMounted: false,
          closeAfterTransition: true,
        },
      },

      // List optimizations
      MuiList: {
        styleOverrides: {
          root: {
            contain: "content",
            willChange: "transform",
            touchAction: "manipulation",
          },
        },
      },

      // ListItem optimizations
      MuiListItem: {
        styleOverrides: {
          root: {
            contain: "content",
            willChange: "transform, background-color",
            touchAction: "manipulation",
          },
        },
      },

      // Menu optimizations
      MuiMenu: {
        styleOverrides: {
          root: {
            contain: "layout",
            touchAction: "none",
          },
          paper: {
            transform: "translateZ(0)",
            contain: "content",
            willChange: "transform",
          },
        },
        defaultProps: {
          disableAutoFocusItem: true,
          disableScrollLock: true,
          keepMounted: false,
        },
      },

      // Popover optimizations
      MuiPopover: {
        styleOverrides: {
          root: {
            contain: "layout",
            touchAction: "none",
          },
          paper: {
            transform: "translateZ(0)",
            contain: "content",
            willChange: "transform",
          },
        },
        defaultProps: {
          disableScrollLock: true,
          keepMounted: false,
        },
      },

      // IconButton optimizations
      MuiIconButton: {
        styleOverrides: {
          root: {
            contain: "content",
            willChange: "transform, background-color",
            touchAction: "manipulation",
            transform: "translateZ(0)",
          },
        },
      },

      // TextField optimizations
      // MuiTextField: {
      //   styleOverrides: {
      //     root: {
      //       contain: "content",
      //     },
      //   },
      // },

      // // Input optimizations
      // MuiInputBase: {
      //   styleOverrides: {
      //     root: {
      //       contain: "content",
      //       touchAction: "manipulation",
      //     },
      //   },
      // },
    },
  }
}

// Create a custom theme using the options
const theme = createTheme(createThemeOptions())
export default theme